import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Loader2,
  Package,
  Check,
  Plus,
  Minus,
  DollarSign,
  Sparkles,
  Shield,
  Users,
  FileText,
  Truck,
  Calculator,
  Settings,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for module categories
const categoryIcons = {
  core: Package,
  hr: Users,
  finance: Calculator,
  operations: Truck,
  compliance: Shield,
  integrations: Settings,
  general: Sparkles,
};

// Color mapping for categories
const categoryColors = {
  core: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  hr: 'bg-blue-100 text-blue-700 border-blue-200',
  finance: 'bg-amber-100 text-amber-700 border-amber-200',
  operations: 'bg-purple-100 text-purple-700 border-purple-200',
  compliance: 'bg-red-100 text-red-700 border-red-200',
  integrations: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  general: 'bg-gray-100 text-gray-700 border-gray-200',
};

export default function ModuleConfiguration() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [config, setConfig] = useState(null);
  const [categories, setCategories] = useState({});
  const [pendingChanges, setPendingChanges] = useState({});
  const [saving, setSaving] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingModule, setPendingModule] = useState(null);
  const [previewCost, setPreviewCost] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [modulesRes, configRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/modules/available`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/modules/config`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/modules/categories`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      
      setModules(modulesRes.data);
      setConfig(configRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching modules:', error);
      toast.error('Failed to load module configuration');
    } finally {
      setLoading(false);
    }
  };

  const isModuleEnabled = (moduleId) => {
    if (pendingChanges[moduleId] !== undefined) {
      return pendingChanges[moduleId];
    }
    return config?.enabled_modules?.some(m => m.module_id === moduleId) || false;
  };

  const handleToggleModule = async (module) => {
    const currentlyEnabled = isModuleEnabled(module.id);
    
    // Can't disable base modules
    if (module.is_base && currentlyEnabled) {
      toast.error('Base modules cannot be disabled');
      return;
    }
    
    // Show confirmation dialog
    setPendingModule({
      ...module,
      action: currentlyEnabled ? 'disable' : 'enable'
    });
    setShowConfirmDialog(true);
  };

  const confirmModuleChange = async () => {
    if (!pendingModule) return;
    
    setSaving(pendingModule.id);
    setShowConfirmDialog(false);
    
    try {
      const endpoint = pendingModule.action === 'enable' 
        ? '/api/modules/enable' 
        : '/api/modules/disable';
      
      const response = await axios.post(
        `${API_URL}${endpoint}`,
        { module_id: pendingModule.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      
      // Refresh data
      fetchData();
      
    } catch (error) {
      console.error('Error toggling module:', error);
      toast.error(error.response?.data?.detail || 'Failed to update module');
    } finally {
      setSaving(null);
      setPendingModule(null);
    }
  };

  const calculatePreviewCost = async (moduleIds) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/modules/preview-cost`,
        moduleIds,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreviewCost(response.data);
    } catch (error) {
      console.error('Error calculating preview cost:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Group modules by category
  const modulesByCategory = Object.entries(categories).map(([key, category]) => ({
    id: key,
    name: category.name,
    modules: category.modules || [],
  }));

  return (
    <div className="container mx-auto p-6 max-w-5xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Package className="h-8 w-8 text-primary" />
            Module Configuration
          </h1>
          <p className="text-muted-foreground mt-1">
            Customize your subscription with the features you need
          </p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Current Cost Summary */}
      <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Current Monthly Cost</p>
              <p className="text-3xl font-bold text-primary">
                ${config?.total_monthly_cost?.toFixed(2) || '0.00'}
                <span className="text-base font-normal text-muted-foreground">/month</span>
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Active Modules</p>
              <p className="text-2xl font-semibold">
                {config?.enabled_modules?.length || 0}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* No Modules Available */}
      {modules.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No Modules Available</h3>
            <p className="text-muted-foreground mb-4">
              Modules are configured in the Stripe Dashboard.
            </p>
            <p className="text-sm text-muted-foreground">
              To set up modules, create Products in Stripe with the following metadata:
            </p>
            <div className="mt-4 bg-muted p-4 rounded-lg text-left max-w-md mx-auto">
              <code className="text-xs">
                module_id: "your-module-id"<br />
                module_category: "core|hr|finance|operations"<br />
                module_features: '["feature1", "feature2"]'<br />
                is_base: "true|false"
              </code>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Module Categories */}
      {modulesByCategory.map((category) => {
        if (category.modules.length === 0) return null;
        
        const CategoryIcon = categoryIcons[category.id] || Package;
        const colorClass = categoryColors[category.id] || categoryColors.general;
        
        return (
          <Card key={category.id}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className={`p-2 rounded-lg ${colorClass}`}>
                  <CategoryIcon className="h-5 w-5" />
                </div>
                {category.name}
              </CardTitle>
              <CardDescription>
                {category.modules.length} module{category.modules.length !== 1 ? 's' : ''} available
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {category.modules.map((module, index) => {
                  const enabled = isModuleEnabled(module.id);
                  const isSaving = saving === module.id;
                  
                  return (
                    <div key={module.id}>
                      {index > 0 && <Separator className="my-4" />}
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold">{module.name}</h4>
                            {module.is_base && (
                              <Badge variant="secondary" className="text-xs">
                                Base
                              </Badge>
                            )}
                            {enabled && (
                              <Badge variant="default" className="text-xs bg-green-600">
                                <Check className="mr-1 h-3 w-3" />
                                Active
                              </Badge>
                            )}
                          </div>
                          {module.description && (
                            <p className="text-sm text-muted-foreground mb-2">
                              {module.description}
                            </p>
                          )}
                          {module.features?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {module.features.map((feature) => (
                                <Badge 
                                  key={feature} 
                                  variant="outline" 
                                  className="text-xs"
                                >
                                  {feature.replace(/_/g, ' ')}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="text-right flex flex-col items-end gap-2">
                          <p className="font-semibold text-lg">
                            ${module.price?.toFixed(2)}
                            <span className="text-sm font-normal text-muted-foreground">
                              /{module.interval}
                            </span>
                          </p>
                          {module.is_base ? (
                            <Badge variant="outline" className="text-xs">
                              Required
                            </Badge>
                          ) : (
                            <Button
                              variant={enabled ? "outline" : "default"}
                              size="sm"
                              onClick={() => handleToggleModule(module)}
                              disabled={isSaving}
                              data-testid={`toggle-module-${module.id}`}
                            >
                              {isSaving ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : enabled ? (
                                <>
                                  <Minus className="mr-1 h-4 w-4" />
                                  Remove
                                </>
                              ) : (
                                <>
                                  <Plus className="mr-1 h-4 w-4" />
                                  Add
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Setup Guide Link */}
      <Card className="bg-muted/30">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-muted-foreground" />
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">
                <strong>Admin:</strong> Modules are managed in your{' '}
                <a 
                  href="https://dashboard.stripe.com/products" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Stripe Dashboard
                </a>
                . Each product with <code className="bg-muted px-1 rounded">module_id</code> metadata will appear here.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pendingModule?.action === 'enable' ? 'Enable' : 'Disable'} Module?
            </DialogTitle>
            <DialogDescription>
              {pendingModule?.action === 'enable' ? (
                <>
                  Adding <strong>{pendingModule?.name}</strong> will increase your monthly subscription by{' '}
                  <strong>${pendingModule?.price?.toFixed(2)}</strong>.
                </>
              ) : (
                <>
                  Removing <strong>{pendingModule?.name}</strong> will reduce your monthly subscription by{' '}
                  <strong>${pendingModule?.price?.toFixed(2)}</strong>.
                  <br /><br />
                  <span className="text-amber-600">
                    Warning: You will lose access to features provided by this module.
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmModuleChange}
              variant={pendingModule?.action === 'disable' ? 'destructive' : 'default'}
              data-testid="confirm-module-change-btn"
            >
              {pendingModule?.action === 'enable' ? (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Module
                </>
              ) : (
                <>
                  <Minus className="mr-2 h-4 w-4" />
                  Remove Module
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
